import {
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ShipByDateCell from 'common/components/ShipByDateCell';
import lineItemStatus from 'common/constants/lineItemStatus';
import orderFillType from 'common/constants/orderFillType';
import { useTheme } from '@mui/material/styles';
import { find, map } from 'lodash';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';
import perfectRxTheme from 'common/theme/perfectRxTheme';

const headCells = [
  {
    id: 'itemName',
    label: 'Drug Name',
  },
  {
    id: 'rxNumber',
    label: 'Rx Number',
  },
  {
    id: 'lineItemStatus',
    label: 'Item Status',
  },
  {
    id: 'fillType',
    label: 'Fill Type',
  },
  {
    id: 'shippingOption',
    label: 'Shipping Option',
  },
  {
    id: 'form',
    label: 'Form',
  },
  {
    id: 'strength',
    label: 'Strength',
  },
  {
    id: 'quantity',
    label: 'Quantity',
  },
  {
    id: 'shipByDate',
    label: 'Ship By Date',
  },
];

const ProductFulfillmentLineItemsRow = ({ lineItemRow, lineItems }) => {
  const theme = useTheme();
  const shippingOptions = useSelector(({ systemConfigs }) => systemConfigs.shippingOptions);

  return (
    <TableContainer sx={{ mb: 2, p: 2, ml: 10 }} key={`lineItemRow-${lineItemRow}`}>
      <Table size='small'>
        <TableHead>
          <TableRow hover>
            {headCells.map((headCell) => (
              <TableCell align='center' key={headCell.id} sx={{ fontWeight: '600' }}>
                {headCell.label}
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>

        <TableBody>
          {map(lineItems, (item) => (
            <TableRow key={item.lineItemId} hover>
              <TableCell align='center'>{item.itemName}</TableCell>
              <TableCell align='center'>{item.rxNumber}</TableCell>
              <TableCell align='center'>{lineItemStatus[item.lineItemStatus]}</TableCell>
              <TableCell align='center'>
                <Chip
                  sx={{
                    background:
                      item.fillType === 'REFILL'
                        ? theme.palette.info.dark
                        : theme.palette.success.dark,
                    color: theme.palette.primary.contrastText,
                    height: 35,
                    width: '70px',
                  }}
                  label={
                    <Typography sx={{ fontSize: '1.1em' }}>
                      {orderFillType[item.fillType]}
                    </Typography>
                  }
                />
              </TableCell>
              <TableCell align='center'>
                {item.shippingOption
                  ? find(shippingOptions, (option) => option.optionCode === item.shippingOption)
                      ?.shippingOption || item.shippingOption
                  : 'NA'}
              </TableCell>
              <TableCell align='center'>{item.form}</TableCell>
              <TableCell align='center'>{item.strength}</TableCell>
              <TableCell align='center'>{item.quantity}</TableCell>
              <TableCell align='center'>
                <ShipByDateCell shipByDate={item.shipByDate} tags={item.tags} />
              </TableCell>
              <TableCell>
                <Tooltip
                  title={
                    item.inBatchShipmentQueue
                      ? batchShippingTooltip({ isBusinessAddress: item.isBusinessAddress })
                      : ''
                  }
                  arrow
                  placement='top'
                >
                  {item.inBatchShipmentQueue ? (
                    <BatchShippingIcon
                      color={perfectRxTheme.palette.warning.warningColour}
                      showBadge={item.isBusinessAddress}
                    />
                  ) : (
                    <Grid>{item.isBusinessAddress && <BusinessAddressIndicator />}</Grid>
                  )}
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

ProductFulfillmentLineItemsRow.propTypes = {
  lineItemRow: PropTypes.string.isRequired,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string,
      itemName: PropTypes.string,
      rxNumber: PropTypes.string,
      strength: PropTypes.string,
      form: PropTypes.string,
      quantity: PropTypes.string,
      fillType: PropTypes.string,
      needsByDate: PropTypes.string,
    })
  ).isRequired,
};

export default ProductFulfillmentLineItemsRow;
